<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>
    <modal-info-pedido />
    <modal-selected-embalagens />
    <modal-buscar-representante />
    <modal-buscar-cliente />
    <modal-buscar-tabela-preco />
    <modal-buscar-cond-pagamento />
    <modal-info-produto />
    <modal-info-produto-new />
    <modal-pic-pedido ref="modalPicPedido" />
    <portal-target name="modal-portal" />
  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig';
import { provideToast } from 'vue-toastification/composition';
import { watch } from '@vue/composition-api';
import useAppConfig from '@core/app-config/useAppConfig';
import ModalInfoPedido from '@/components/modal/ModalInfoPedido.vue';
import ModalBuscarRepresentante from '@/components/modal/ModalBuscarRepresentante.vue';
import ModalBuscarCliente from '@/components/modal/ModalBuscarCliente.vue';
import ModalBuscarTabelaPreco from '@/components/modal/ModalBuscarTabelaPreco.vue';
import ModalBuscarCondPagamento from '@/components/modal/ModalBuscarCondPagamento.vue';
import ModalInfoProduto from '@/components/modal/ModalInfoProduto.vue';
import ModalInfoProdutoNew from '@/components/modal/ModalInfoProdutoNew.vue';
import ModalPicPedido from '@/components/modal/modal_pic_pedido.vue';
import InfoPedidoService from '@/services/InfoPedido.service';
import prioridadePedidoService from '@/services/SistemaDKP/SLSEsPriori.service';
import SLSMarcaService from '@/services/SistemaDKP/sls-marca.service';
import { useWindowSize, useCssVar } from '@vueuse/core';

import store from '@/store';
import ModalSelectedEmbalagens from '@/components/modal/ModalEmbalagens.vue';
import Swal from 'sweetalert2';
import { PortalTarget } from 'portal-vue';
import ToastificationContent from './@core/components/toastification/ToastificationContent.vue';

const infoPedidoService = new InfoPedidoService();
const prioridadePedido = new prioridadePedidoService();
const slsMarcaService = new SLSMarcaService();

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue');
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue');
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue');

export default {
  components: {
    ModalSelectedEmbalagens,
    ModalInfoPedido,
    ModalBuscarRepresentante,
    ModalBuscarCliente,
    ModalBuscarTabelaPreco,
    ModalBuscarCondPagamento,
    ModalInfoProduto,
    ModalInfoProdutoNew,
    ModalPicPedido,
    PortalTarget,
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full';
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
  },
  async beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark'];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2));
    }
    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
  },
  watch: {
    mostrarModalInfoPedido(val) {
      if (!val) {
        this.$store.dispatch('InfoPedido/prepararModalEmbalagens');
      }
    },
  },
  methods: {
    async abrirModalProduto(produtoId) {
      const preenchido = this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'];

      if (preenchido) {
        await this.$store.dispatch('InfoPedido/prepararModalEmbalagens');
        this.abrirModalEmbalagens(produtoId);
      } else {
        this.abrirModalInfoPedido();
      }
    },
    abrirModalInfoPedido() {
      this.mostrarModalInfoPedido = true;
    },
    abrirModalEmbalagens(produtoId) {
      // Verifica se dados estão prontos para abrir rapidamente o modal
      if (this.$store.state.InfoPedido.itemSelected === produtoId) {
        this.mostrarModalEmbalagens = true;
      }
    },
    async onClickItem() {
      if (!this.$store.getters['auth/IS_LOGGED_IN']) {
        await Swal.fire({
          icon: 'info',
          title: 'Usuário não autenticado',
          text: 'Por favor, faça login para continuar.',
          confirmButtonText: 'Login',
        });
        await this.$router.push({ name: 'login' });
        return;
      }

      try {
        Swal.fire({
          title: 'Carregando...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        const loadingTimer = setTimeout(() => {
          try {
            if (Swal.isVisible()) Swal.close();
          } catch (e) {
            // noop
          }
        }, 15000);

        try {
          const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));
          const isClienteFinal = perfilLogado && perfilLogado.perfil_acesso_nome === 'ClienteFinal';
          const firstClickFlag = localStorage.getItem('firstClickClienteFinal') === 'true';
          if (isClienteFinal && firstClickFlag) {
            localStorage.setItem('firstClickClienteFinal', 'false');
            try { if (Swal.isVisible()) Swal.close(); } catch (e) {}
            this.$bvModal.show('modal-center-info-pedido');
            return;
          }
        } catch (e) {
          console.error('Erro ao verificar perfil do usuário:', e);
        }

        let validation = this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'];

        if (!validation) {
          const userId = this.getUserId();
          const savedFormData = localStorage.getItem(`infoPedidoForm_${userId}`);

          if (savedFormData) {
            try {
              const parsedData = JSON.parse(savedFormData);

              this.$store.commit('InfoPedido/SET_FORM_INFO_PEDIDO', parsedData);

              this.$store.commit('InfoPedido/SET_CLIENTE_DATA', {
                clienteData: parsedData,
                enderecoEntrega: parsedData.enderecoEntrega,
                dataCapaPedido: {
                  estabelecimentoid: parsedData.estabelecimento?.estabelecimentoid,
                  clientenomeabreviado: parsedData.cliente?.clientenomeabreviado,
                  naturezaoperacaoid: parsedData.naturezaOperacao?.naturezaoperacaoid || parsedData.cliente?.naturezaoperacaoid,
                  canal_venda_id: parsedData.cliente?.canal_venda_id,
                  clienteisentoie: parsedData.cliente?.clienteisentoie,
                  tabelaprecoid: parsedData.tabelaPreco?.tabelapreco_id,
                  condicaopagamentoid: parsedData.condicaoPagamento?.condicaopagamentoid,
                },
              });

              validation = this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'];

              if (validation) {
                this.$store.commit('InfoPedido/SET_IS_CONFIRMA_PEDIDO', true);
              }
            } catch (error) {
              console.error('Erro ao carregar dados salvos:', error);
            }
          }
        }

        if (!validation) {
          console.log('ABRIR MODAL INFO PEDIDO');
          try { if (Swal.isVisible()) Swal.close(); } catch (e) {}
          this.$bvModal.show('modal-center-info-pedido');
        } else {
          console.log('ABRIR MODAL EMBALAGENS');
          this.$bvModal.hide('modal-center-info-pedido');
          await this.hydrateSelectedItemImages();
          await infoPedidoService.getEmbalagemByFamComercialId();
          if (this.$store.getters['slsEmbalagem/getEsEmbalg'].length === 0) {
            await Swal.fire({
              icon: 'error',
              title: 'Embalagens',
              text: 'Não há embalagens disponíveis para o produto! Entre em contato com ADM Vendas.',
            });
          } else {
            this.$bvModal.show('modal-top-selected-embalagens');
          }
        }
      } catch (error) {
        console.error('Erro ao processar o clique:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao validar informações',
            text: 'Verifique as Configurações do Usuário.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
      } finally {
        try { clearTimeout(loadingTimer); } catch (e) {}
        Swal.close();
      }
    },

    async hydrateSelectedItemImages() {
      const item = this.$store.getters['InfoPedido/GET_ITEM_SELECTED'];
      if (!item?.cod_marca || !item?.familia_comercial_id) return;

      try {
        const response = await slsMarcaService.listarImagensPorMarcaRaw(item.cod_marca, {
          familia_comercial_id: item.familia_comercial_id,
        });

        const data = Array.isArray(response) ? response : response?.data || [];
        const fullItem = data[0];

        if (fullItem?.imagens?.length) {
          this.$store.commit('InfoPedido/SET_ITEM_SELECTED', {
            ...item,
            imagens: fullItem.imagens,
          });
        }
      } catch (error) {
        console.error('Erro ao carregar imagens completas do item:', error);
      }
    },

    getUserId() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData'));
        return userData?.id || 'default';
      } catch (error) {
        console.error('Erro ao obter ID do usuário:', error);
        return 'default';
      }
    },
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout');

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    });

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val);
    });

    return {
      skinClasses,
    };
  },
  async mounted() {
    await prioridadePedido.selectPrioridadePedido();

    if (this.$store.getters['auth/IS_LOGGED_IN']) {
      try {
        const userData = JSON.parse(localStorage.getItem('userData'));
        const userId = userData?.id || 'default';
        const savedFormData = localStorage.getItem(`infoPedidoForm_${userId}`);

        if (savedFormData) {
          const parsedData = JSON.parse(savedFormData);
          this.$store.commit('InfoPedido/SET_FORM_INFO_PEDIDO', parsedData);

          if (parsedData.cliente && parsedData.cliente.clienteid) {
            this.$store.commit('InfoPedido/SET_CLIENTE_DATA', {
              clienteData: parsedData,
              enderecoEntrega: parsedData.enderecoEntrega,
              dataCapaPedido: {
                estabelecimentoid: parsedData.estabelecimento?.estabelecimentoid,
                clientenomeabreviado: parsedData.cliente?.clientenomeabreviado,
                naturezaoperacaoid: parsedData.naturezaOperacao?.naturezaoperacaoid || parsedData.cliente?.naturezaoperacaoid,
                canal_venda_id: parsedData.cliente?.canal_venda_id,
                clienteisentoie: parsedData.cliente?.clienteisentoie,
                tabelaprecoid: parsedData.tabelaPreco?.tabelapreco_id,
                condicaopagamentoid: parsedData.condicaoPagamento?.condicaopagamentoid,
              },
            });
          }

          if (this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO']) {
            this.$store.commit('InfoPedido/SET_IS_CONFIRMA_PEDIDO', true);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados do pedido do localStorage:', error);
      }
    }

    this.$root.$on('infoPedido:checkGrade', async () => {
      await this.onClickItem();
    });
  },
  destroyed() {
    this.$root.$off('infoPedido:checkGrade');
  },
};
</script>
